import React, {useEffect, useState, useMemo, useCallback} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {NavBar, SearchBar, Tag, Button, CalendarPicker, Popup, Divider} from "antd-mobile";
import { getNavBarContainerStyle, isAndroid } from "../../../utils/deviceUtils";
import {SearchOutline, DownOutline} from 'antd-mobile-icons';
import EmptyState from "../../../components/EmptyState";
import i18n, {t} from "i18n";
import {formatTimestampToDateTime, formatTimestampToSecondsTime, formatTimestampByLanguage} from "utils/dateTimeUtils";
import {formatLocalizedDate} from "../../../utils/dateFormatUtils";
import {PickupAppointmentType} from "../graphql/types/PickupTaskWithAppointmentsType";
import {getUnitLabel} from "../../../enums/PickupEnum";
import dayjs from "dayjs";
import {
    useCompletedPickupTaskAppointmentList,
} from "../store/pickupStore";
import useHistoryControl from "../../../hooks/useHistoryControl";
import {buildAddress1AndAddress2} from "../../../utils/addressUtils";

interface PickupFinishedProps {
}

const PickupFinishedPage: React.FC<PickupFinishedProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showSearch, setShowSearch] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [searchValue, setSearchValue] = useState<string>('');
    
    // 从路由状态中获取返回的日期，如果没有则使用当前日期
    const returnedSelectedDate = location.state?.selectedDate;
    const initialDate = returnedSelectedDate ? dayjs(returnedSelectedDate).toDate() : new Date();
    const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
    const [finishedAppointments, setFinishedAppointments] = useState<PickupAppointmentType[][]>([]);
    
    // 参考PickingUpPage.tsx的实现方式，添加loading状态
    const selectedDateStr = selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD').toString() : dayjs().format('YYYY-MM-DD').toString();
    const {pickupTaskAppointmentList: pickingUpData, loading, refreshPickupTaskAppointmentList} = useCompletedPickupTaskAppointmentList(selectedDateStr)
    useHistoryControl("/pickup/picking-up", {
        useReplace: true,
        controlNavButton: false
    });
    useEffect(() => {
        // 当data存在时，更新本地状态
        if (pickingUpData.length >= 0){
            setFinishedAppointments(pickingUpData);
        }
    }, [pickingUpData]);

    // 添加弱网处理：当数据为空且不在加载状态时，尝试重新获取数据
    useEffect(() => {
        refreshPickupTaskAppointmentList(selectedDateStr);
    }, [selectedDateStr]);

    // 处理从详情页返回时的日期状态更新
    useEffect(() => {
        if (returnedSelectedDate) {
            const newDate = dayjs(returnedSelectedDate).toDate();
            setSelectedDate(newDate);
        }
    }, [returnedSelectedDate]);

    const groupedAppointments = useMemo(() => {
        // 直接使用二维数组数据，每个任务对应一个预约单列表
        return finishedAppointments.map((taskAppointments, taskIndex) => {
            // 对每个任务内的预约单进行过滤和排序
            const filteredTaskAppointments = taskAppointments
                .map((appointment, index) => ({...appointment, originalIndex: index, isLastItem: index === taskAppointments.length - 1}))
                .filter(appointment => (searchValue === '') || appointment.channelName.toLowerCase().includes(searchValue.toLowerCase()))
                .sort((a, b) => {
                    // start_point 排在最前面
                    if (a.status === 'start_point') return -1;
                    if (b.status === 'start_point') return 1;

                    // 获取比较时间
                    const getValidTime = (appointment: PickupAppointmentType) => {
                        const timeStr = appointment.status === 'end_point'
                        || appointment.status === 'CANCELLED' ||  appointment.status === 'TRANSFER_OUT'
                            ? formatTimestampToSecondsTime(appointment.unloadingTime)
                            : formatTimestampToSecondsTime(appointment.actualPickupTime);
                        const time = new Date(timeStr).getTime();
                        return isNaN(time) ? Number.MAX_SAFE_INTEGER : time; // 处理 Invalid Date
                    };

                    const aTime = getValidTime(a);
                    const bTime = getValidTime(b);

                    return aTime - bTime;
                });
            
            return {
                taskId: taskAppointments[0]?.taskId,
                appointments: filteredTaskAppointments
            };
        }).filter(group => group.appointments.length > 0); // 只显示有预约单的任务组
    }, [finishedAppointments, searchValue, selectedDate]);

    const handleDateSelect = (date: Date | null) => {
        setSelectedDate(date);
        setShowCalendar(false);
        // 当日期变化时，重新查询数据
        const selectedDateStr = date ? dayjs(date).format('YYYY-MM-DD').toString() : dayjs().format('YYYY-MM-DD').toString();
        refreshPickupTaskAppointmentList(selectedDateStr);
    };

    const clearFilters = () => {
        setSearchValue('');
        setSelectedDate(null);
        // 当清除过滤器时，重新查询数据
        refreshPickupTaskAppointmentList(dayjs().format('YYYY-MM-DD').toString());
    };

    const isFiltering = searchValue !== '' || selectedDate !== null;

    const getStatusTag = (appointment: PickupAppointmentType) => {
        if (appointment.status === 'start_point') {
            return null; // 起点不显示标签
        }
        if (appointment.status === 'end_point') {
            return <Tag color="success"
                        className="rounded-full">{t('pages.pickUp.finished.status.handoverSuccess')}</Tag>;
        }
        if (appointment.status === "CANCELLED" || appointment.status === "TRANSFER_OUT") {
            return <Tag color="default"
                        className="bg-gray-200 text-gray-600">{t('pages.pickUp.finished.status.cancelled')}</Tag>;
        }
        if (appointment.isFinished && !appointment.isAbnormal) {
            return <Tag color="success" className="rounded-full">{t('pages.pickUp.finished.status.success')}</Tag>;
        }
        if (appointment.isFinished && appointment.isAbnormal) {
            return <Tag color="danger" className="rounded-full">{t('pages.pickUp.finished.status.failed')}</Tag>;
        }
        return null;
    };

    const handleAppointmentClick = (appointment: PickupAppointmentType, index: number) => {
        const formattedDate = selectedDate? dayjs(selectedDate).format('YYYY-MM-DD'):dayjs().format('YYYY-MM-DD');
        navigate(`/pickup/finished/detail/${appointment.id}/${appointment.taskId}/${formattedDate}`);
        return;
    };

    return (
        <div className="flex flex-col h-screen bg-gray-100">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                    onBack={() => navigate("/pickup/picking-up")}
                    right={
                        <div className="flex items-center justify-end">
                            {isFiltering && (
                                <Button
                                    color='primary'
                                    fill='none'
                                    onClick={clearFilters}
                                    className="rounded-full"
                                >
                                    {t('pages.pickUp.finished.clear')}
                                </Button>
                            )}
                            <Button
                                color='primary'
                                fill='none'
                                onClick={() => setShowSearch(true)}
                                className="rounded-full"
                            >
                                <SearchOutline/>
                            </Button>
                        </div>
                    }
                >
                    {t('pages.pickUp.finished.title')}
                </NavBar>
            </div>

            {/* 搜索弹出框 */}
            <Popup
                visible={showSearch}
                onMaskClick={() => setShowSearch(false)}
                position='top'
                bodyStyle={{
                    borderBottomLeftRadius: '1rem',
                    borderBottomRightRadius: '1rem',
                    minHeight: '3.75em',
                    padding: '0.75em',
                    top: isAndroid() ? '24px' : '0px' // 为Android设备添加顶部偏移
                }}
            >
                <SearchBar
                    className="w-full"
                    placeholder={t('pages.pickUp.finished.searchPlaceholder')}
                    value={searchValue}
                    onChange={setSearchValue}
                    onCancel={() => {
                        setShowSearch(false);
                    }}
                />
            </Popup>

            {/* 日期选择器 */}
            <div className="p-3 bg-white mb-2 shadow-sm">
                <Button
                    block
                    fill='none'
                    onClick={() => setShowCalendar(true)}
                    className="rounded-full"
                >
                    <div className="flex items-center justify-center w-full">
                        <span className="mr-1">
                            {selectedDate ? formatLocalizedDate(selectedDate) : t('pages.pickUp.finished.selectDate')}
                        </span>
                        <DownOutline/>
                    </div>
                </Button>
            </div>

            <CalendarPicker
                visible={showCalendar}
                onClose={() => setShowCalendar(false)}
                onConfirm={handleDateSelect}
                defaultValue={selectedDate}
                selectionMode="single"
                title={t('pages.pickUp.finished.selectDate')}
            />

            <div className="flex-1 overflow-y-auto px-3">
                {groupedAppointments.length > 0 ? (
                    <div className="space-y-3">
                        {groupedAppointments.map((taskGroup, taskIndex) => (
                            <div key={`task-${taskGroup.taskId}-${taskIndex}`} className="space-y-2">
                                {/* 任务标题 */}
                                <div className="bg-white p-2 rounded-lg">
                                    <div className="text-sm font-medium text-black text-center">
                                        {t('pages.pickUp.finished.taskGroup')} {taskIndex+1}
                                    </div>
                                </div>
                                {/* 任务内的预约单 */}
                                {taskGroup.appointments.map((appointment, index) => (
                                    <div
                                        key={index}
                                        className={`bg-white my-2 rounded-lg shadow-sm ${
                                            (appointment.status === 'CANCELLED' || appointment.status === 'TRANSFER_OUT') ? 'bg-gray-200 opacity-60 cursor-not-allowed' : 'cursor-pointer'
                                        }`}
                                        onClick={() => (appointment.status !== 'CANCELLED' && appointment.status !== 'TRANSFER_OUT') && handleAppointmentClick(appointment, index)}
                                    >
                                <div className="p-4">
                                    <div className="flex justify-between items-center break-all">
                                        <div className="text-base font-medium">{appointment.channelName}</div>
                                        {getStatusTag(appointment)}
                                    </div>
                                    <Divider/>
                                    <div className="flex gap-6">
                                        <div className="flex flex-col w-14 items-center justify-center gap-0.5">
                                            <div className="text-3xl font-bold">
                                                {appointment.status === 'end_point' ? (
                                                    <span className="text-xl whitespace-nowrap">{t('pages.pickUp.end')}{(+appointment.id+1)}</span>
                                                ) : appointment.status === 'start_point' ? (
                                                    <span className="text-xl whitespace-nowrap">{t('pages.pickUp.start')}</span>
                                                ) : (
                                                    appointment.sequence
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <p className="text-base break-all">{buildAddress1AndAddress2(appointment)}</p>
                                                {appointment.status === 'start_point' ? (
                                                <div className="text-sm text-gray-500">
                                                    {t('pages.pickUp.finished.actualArrivalTime')}  : {formatTimestampByLanguage(appointment.actualTime, i18n.language)}
                                                </div>
                                            ) : (appointment.status !== 'CANCELLED' && appointment.status !== 'TRANSFER_OUT' )? (
                                                <div className="text-sm text-gray-500">
                                                    {appointment.status === 'end_point' ? (
                                                        <>{t('pages.pickUp.finished.handoverCompletedTime')}: {formatTimestampByLanguage(appointment.unloadingTime, i18n.language)}</>
                                                    ) : appointment.isFinished && !appointment.isAbnormal ? (
                                                        <>{t('pages.pickUp.finished.pickupCompletedTime')}: {formatTimestampByLanguage(appointment.actualPickupTime, i18n.language)}</>
                                                    ) : (
                                                        <>{t('pages.pickUp.finished.failureTime')}: {formatTimestampByLanguage(appointment.actualPickupTime, i18n.language)}</>
                                                    )}
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="text-sm text-gray-500">
                                                        {t('pages.pickUp.pickingUp.appointmentPickUp')}: {appointment.quantity} {getUnitLabel(Number(appointment?.unit), i18n.language)}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {t('pages.pickUp.pickingUp.appointmentPickUpDate')}: {formatTimestampByLanguage(appointment.appointmentTime, i18n.language)}
                                                    </div>
                                                </>
                                            )}
                                            <div className="text-sm text-gray-500" hidden={!appointment.remark}>
                                                {t('pages.pickUp.pickingUp.remark')}: {appointment.remark && appointment.remark.length > 20
                                                ? `${appointment.remark.substring(0, 25)}...`
                                                : appointment.remark}
                                            </div>
                                        </div>



                                    </div>
                                </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <EmptyState/>
                )}
            </div>
        </div>
    );
};

export default PickupFinishedPage;
